/**
 * Import della rosa (V09): i giocatori di un'altra squadra-stagione entrano in
 * questa con nuovi tesseramenti, da oggi.
 *
 * Schermata unica: si sceglie l'origine, si tolgono le spunte a chi non
 * prosegue, si decide se azzerare i numeri. Chi e gia in questa rosa o e
 * archiviato resta visibile ma non si puo scegliere, con il motivo accanto.
 * L'import e tutto o niente; un numero gia occupato qui non lo ferma, e
 * l'esito dice chi e entrato senza.
 */

import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { puo } from '@calcio/shared';
import { CircleCheckIcon, OctagonXIcon } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Field, FieldDescription, FieldLabel } from '@/components/ui/field';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { PulsanteInvio } from '../comuni/Azioni.js';
import { chiama } from '../api/client.js';
import { StatoCaricamento, Testata } from '../comuni/Testata.js';
import { Corpo } from '../comuni/Corpo.js';
import { dataBreve, messaggio, useDati } from '../comuni/dati.js';
import { useContesto } from '../navigazione/contesto.js';
import { NonTrovata } from '../navigazione/NonTrovata.js';
import { type RigaImport, annoDi, oggi, originiImport, preselezioneImport } from './elenchi.js';
import { StatoVuoto } from '../comuni/StatoVuoto.js';

interface Esito {
  creati: number;
  senzaNumero: string[];
}

const MOTIVI: Record<NonNullable<RigaImport['escluso']>, string> = {
  gia_in_rosa: 'Già in questa rosa',
  archiviato: 'Archiviato',
};

export function ImportRosa() {
  const { ss } = useParams<{ ss: string }>();
  const { utente, squadre, ricaricaSquadre } = useContesto();
  const [origine, setOrigine] = useState('');
  const [esito, setEsito] = useState<Esito | null>(null);

  // Il guscio lascia passare chi vede la rosa: importare e dell'Admin.
  if (!puo(utente.assegnazioni, 'tesseramento.gestisci', ss)) return <NonTrovata />;

  const rosa = `/sq/${ss}/rosa`;
  const indietro = `/sq/${ss}`;

  if (esito) {
    return (
      <div>
        <Testata titolo="Importa rosa" indietro={indietro} />
        <Corpo className="flex flex-col gap-4">
          <Alert>
            <CircleCheckIcon />
            <AlertTitle>
              {esito.creati === 1
                ? 'Creato 1 tesseramento.'
                : `Creati ${esito.creati} tesseramenti.`}
            </AlertTitle>
            {esito.senzaNumero.length > 0 && (
              <AlertDescription>
                Numero già occupato in questa rosa, entrati senza: {esito.senzaNumero.join(', ')}.
                Il numero si assegna dalla rosa.
              </AlertDescription>
            )}
          </Alert>
          <div>
            <Button asChild>
              <Link to={rosa}>Vai alla rosa</Link>
            </Button>
          </div>
        </Corpo>
      </div>
    );
  }

  const origini = originiImport(squadre ?? [], ss!);

  return (
    <div>
      <Testata titolo="Importa rosa" indietro={indietro} />
      <Corpo className="flex flex-col gap-6">
        <Field>
          <FieldLabel htmlFor="import-origine">Rosa di origine</FieldLabel>
          <Select value={origine} onValueChange={setOrigine}>
            <SelectTrigger id="import-origine" className="w-full @xl:max-w-sm">
              <SelectValue placeholder="Scegli squadra e stagione" />
            </SelectTrigger>
            <SelectContent>
              {origini.map(([stagione, elenco]) => (
                <SelectGroup key={stagione}>
                  <SelectLabel>{stagione}</SelectLabel>
                  {elenco.map((s) => (
                    <SelectItem key={s.id} value={s.id}>
                      {s.squadra} · {s.tesserati} tesserati
                    </SelectItem>
                  ))}
                </SelectGroup>
              ))}
            </SelectContent>
          </Select>
          <FieldDescription>
            Si creano solo nuovi tesseramenti: nessun dato dei giocatori viene duplicato, e lo
            storico li segue.
          </FieldDescription>
        </Field>

        {origine && (
          <Anteprima
            key={origine}
            ss={ss!}
            origine={origine}
            onImportato={async (e) => {
              setEsito(e);
              // Il numero di tesserati compare nel selettore e nella scelta della squadra.
              await ricaricaSquadre();
            }}
          />
        )}
      </Corpo>
    </div>
  );
}

function Anteprima({
  ss,
  origine,
  onImportato,
}: {
  ss: string;
  origine: string;
  onImportato: (esito: Esito) => Promise<void>;
}) {
  const { dati: righe, errore } = useDati<RigaImport[]>(
    `/squadre-stagione/${ss}/import?origine=${origine}`,
  );
  const [scelti, setScelti] = useState<Set<string> | null>(null);
  const [azzeraNumeri, setAzzeraNumeri] = useState(false);
  const [erroreServer, setErroreServer] = useState<string | null>(null);
  const [inCorso, setInCorso] = useState(false);

  if (!righe) return <StatoCaricamento errore={errore} />;
  if (righe.length === 0) {
    return <StatoVuoto titolo="La rosa di origine non ha giocatori" />;
  }

  // Finche non si tocca nulla vale la preselezione: i dati arrivano dopo il primo render.
  const selezione = scelti ?? preselezioneImport(righe, oggi());
  const cambia = (id: string, spuntato: boolean) => {
    const nuova = new Set(selezione);
    if (spuntato) nuova.add(id);
    else nuova.delete(id);
    setScelti(nuova);
  };

  const importa = async () => {
    setErroreServer(null);
    setInCorso(true);
    try {
      const esito = await chiama<Esito>(`/squadre-stagione/${ss}/import`, {
        metodo: 'POST',
        corpo: { origineId: origine, giocatori: [...selezione], azzeraNumeri },
      });
      await onImportato(esito);
    } catch (e) {
      setErroreServer(messaggio(e));
      setInCorso(false);
    }
  };

  const n = selezione.size;

  return (
    <div className="flex flex-col gap-4">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-10">
              <span className="sr-only">Importa</span>
            </TableHead>
            <TableHead className="w-12 text-right">N.</TableHead>
            <TableHead>Giocatore</TableHead>
            <TableHead>Anno</TableHead>
            <TableHead>Note</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {righe.map((r) => {
            const idCasella = `import-${r.giocatoreId}`;
            const nota = r.escluso
              ? MOTIVI[r.escluso]
              : r.dataFine
                ? `Tesseramento chiuso il ${dataBreve(r.dataFine)}`
                : null;
            return (
              <TableRow key={r.giocatoreId} data-disabled={r.escluso !== null || undefined}>
                <TableCell>
                  <Checkbox
                    id={idCasella}
                    checked={r.escluso === null && selezione.has(r.giocatoreId)}
                    disabled={r.escluso !== null}
                    onCheckedChange={(v) => cambia(r.giocatoreId, v === true)}
                  />
                </TableCell>
                <TableCell
                  className={
                    azzeraNumeri
                      ? 'text-right text-muted-foreground line-through tabular-nums'
                      : 'text-right tabular-nums'
                  }
                >
                  {r.numeroMaglia ?? '—'}
                </TableCell>
                <TableCell>
                  <Label
                    htmlFor={idCasella}
                    className={r.escluso ? 'font-normal text-muted-foreground' : 'font-medium'}
                  >
                    {r.cognome} {r.nome}
                  </Label>
                </TableCell>
                <TableCell className="tabular-nums">{annoDi(r.dataNascita)}</TableCell>
                <TableCell className="text-sm text-muted-foreground">{nota}</TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>

      <div className="flex min-h-9 items-center gap-2">
        <Switch id="import-azzera" checked={azzeraNumeri} onCheckedChange={setAzzeraNumeri} />
        <Label htmlFor="import-azzera" className="font-normal">
          Azzera i numeri di maglia
        </Label>
      </div>

      {erroreServer && (
        <Alert variant="destructive">
          <OctagonXIcon />
          <AlertTitle>Salvataggio non riuscito</AlertTitle>
          <AlertDescription>{erroreServer}</AlertDescription>
        </Alert>
      )}

      <div className="flex flex-wrap items-center justify-between gap-3 border-t pt-4">
        <p className="text-sm text-muted-foreground">
          {n === 0
            ? 'Nessun giocatore scelto.'
            : `${n === 1 ? 'Verrà creato 1 tesseramento' : `Verranno creati ${n} tesseramenti`}, da oggi.`}
        </p>
        <PulsanteInvio
          type="button"
          inCorso={inCorso}
          disabled={n === 0}
          onClick={() => void importa()}
        >
          Importa
        </PulsanteInvio>
      </div>
    </div>
  );
}
